/**
 * Hand-maintained corrections to the raw ratings dataset.
 *
 * The raw data is in good shape (616 unique names, no encoding issues), so this
 * file stays small. Add to it rather than editing raw/ratings.json, so the raw
 * file can always be regenerated from the ratings pipeline without losing fixes.
 */

/**
 * Misspellings in the raw data.
 *
 * `Fin Allen` -> `Finn Allen` is the important one: it is not cosmetic. The same
 * player appears as "Fin Allen" (2021 Punjab) and "Finn Allen" (2026 Kolkata),
 * which would let a user draft him twice despite the no-duplicates rule.
 * Everything else here is display polish.
 */
export const NAME_OVERRIDES = {
  'Mattew Hayden': 'Matthew Hayden',
  'Fin Allen': 'Finn Allen',
  'Grame Smith': 'Graeme Smith',
  'DArcy Short': "D'Arcy Short",
  'Loki Ferguson': 'Lockie Ferguson',
  'Venky Iyer': 'Venkatesh Iyer',
};

/**
 * Franchise name -> neutral city label.
 *
 * We deliberately do not ship official franchise names or logos (see plan §1,
 * IP posture). Squads render as "Chennai 2016" instead.
 *
 * Six cities host two franchises across the dataset's history, but no pair
 * overlaps in years, and every label is displayed with its season, so
 * "Hyderabad 2011" (Deccan) and "Hyderabad 2016" (Sunrisers) never collide.
 * `assertNoCityYearCollision` in build-roster.mjs enforces this.
 */
export const CITY_LABELS = {
  'Chennai Super Kings': 'Chennai',
  'Deccan Chargers': 'Hyderabad',
  'Delhi Capitals': 'Delhi',
  'Delhi Daredevils': 'Delhi',
  'Gujarat Lions': 'Gujarat',
  'Gujarat Titans': 'Gujarat',
  'Kings XI Punjab': 'Punjab',
  'Kochi Tuskers Kerala': 'Kochi',
  'Kolkata Knight Riders': 'Kolkata',
  'Lucknow Super Giants': 'Lucknow',
  'Mumbai Indians': 'Mumbai',
  'Pune Warriors': 'Pune',
  'Punjab Kings': 'Punjab',
  'Rajasthan Royals': 'Rajasthan',
  'Rising Pune Supergiant': 'Pune',
  'Rising Pune Supergiants': 'Pune',
  'Royal Challengers Bangalore': 'Bengaluru',
  'Royal Challengers Bengaluru': 'Bengaluru',
  'Sunrisers Hyderabad': 'Hyderabad',
};

/** Player role codes, interned as integers in the shipped artifact. */
export const ROLE_CODES = { BAT: 0, BWL: 1, AR: 2, WK: 3 };

/** Bit flags packed into the per-player `flags` byte. */
export const FLAG = {
  INDIAN: 1 << 0,
  ORANGE_CAP: 1 << 1,
  PURPLE_CAP: 1 << 2,
};
